import {
  formatDurationMs,
  leaderboardPoints,
  modeTitle,
} from "../../utils/calendar";

export interface GameResultState {
  mode: string;
  score: number;
  total: number;
  durationMs: number;
  rank: number | null;
  error: string | null;
}

function resultCopy(mode: string, score: number, total: number): {
  title: string;
  subtitle: string;
} {
  const perfect = total > 0 && score === total;
  const percent = total > 0 ? Math.round((score / total) * 100) : 0;

  if (mode === "whos_with_who") {
    if (perfect) return { title: "Perfect pairs!", subtitle: "You matched every couple." };
    if (percent >= 80) return { title: "Strong memory!", subtitle: "You finished the round." };
    if (percent >= 50) return { title: "Getting there!", subtitle: "Keep practicing those pairs." };
    return { title: "Keep practicing", subtitle: "Try again when you're ready." };
  }

  if (mode === "family_match") {
    if (perfect) {
      return {
        title: "Great job!",
        subtitle: `You matched all ${total} family branches.`,
      };
    }
    return {
      title: "Nice work!",
      subtitle: `${score} of ${total} branches matched.`,
    };
  }

  if (mode === "family_lore") {
    if (perfect) {
      return {
        title: "Great job!",
        subtitle: "Trivia legend — a perfect round!",
      };
    }
    return { title: "Nice work!", subtitle: "You finished the round." };
  }

  if (perfect) {
    return {
      title: "Great job!",
      subtitle: `You nailed every face in ${modeTitle(mode)}.`,
    };
  }
  return {
    title: "Nice work!",
    subtitle: "Try again to improve your score.",
  };
}

export function GameResults({
  result,
  onPlayAgain,
  onBackToGames,
  onSeeRankings,
}: {
  result: GameResultState;
  onPlayAgain: () => void;
  onBackToGames: () => void;
  onSeeRankings: () => void;
}) {
  const { mode, score, total, durationMs, rank, error } = result;
  const perfect = total > 0 && score === total;
  const percent = total > 0 ? (score / total) * 100 : 0;
  const points = leaderboardPoints(percent, durationMs);
  const { title, subtitle } = resultCopy(mode, score, total);
  const ringValue = perfect ? "100%" : String(score);
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(1, Math.max(0, percent / 100));

  return (
    <div className="game-results">
      <div className="results-hero">
        <p className="results-mode caps muted">{modeTitle(mode)}</p>
        <h1 className="results-title">
          {title}
          {perfect ? <span aria-hidden> 🎉</span> : null}
        </h1>
        <p className="results-subtitle">{subtitle}</p>
      </div>

      <div className={`results-ring-wrap${perfect ? " is-perfect" : ""}`}>
        <svg className="results-ring" viewBox="0 0 128 128" aria-hidden>
          <circle cx="64" cy="64" r={radius} className="results-ring-track" />
          <circle
            cx="64"
            cy="64"
            r={radius}
            className="results-ring-progress"
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: circumference * (1 - progress),
            }}
          />
        </svg>
        <div className="results-ring-label">
          <span className="results-ring-value">{ringValue}</span>
          {!perfect ? <span className="results-ring-of">of {total}</span> : null}
        </div>
      </div>

      <div className="results-meta">
        <span>
          Got {score} of {total}
          {perfect ? " · Perfect!" : ""}
        </span>
        <span className="results-meta-dot" aria-hidden>
          ·
        </span>
        <span>{formatDurationMs(durationMs)}</span>
      </div>

      {error ? <p className="error-text results-error">{error}</p> : null}

      {rank != null && !error ? (
        <button type="button" className="results-rank-chip" onClick={onSeeRankings}>
          You&apos;re #{rank} · {points} pts
        </button>
      ) : null}

      <div className="results-actions">
        <button type="button" className="btn-primary" onClick={onPlayAgain}>
          {perfect ? "Play again" : "Try again"}
        </button>
        <button type="button" className="btn-ghost" onClick={onSeeRankings}>
          See family rankings
        </button>
        <button type="button" className="btn-ghost" onClick={onBackToGames}>
          Back to games
        </button>
      </div>
    </div>
  );
}
